import { Plus, Paperclip, Users, Building2, Globe2, Trash2, Printer } from "lucide-react";
import { caseStatusLabel, caseStatusVariant, formatInstant, progressPercent } from "./mouCaseShared.js";

/**
 * P164-R1: a company's MOU rounds, as a grid of cards.
 *
 * Owner: "แสดงเป็น grid ของ profile แต่ละรอบที่จะขอแรงงาน ... อย่าเอาประเทศตั้ง
 * ให้เอารอบตั้ง แต่ละรอบจะเป็นแค่ประเทศเดียวเท่านั้น".
 *
 * So the ROUND is the subject of every card - "รอบที่ 3" is the headline, and
 * the country is one attribute of that round beside the employer and the head
 * count. Grouping by country instead would have been the wrong shape twice
 * over: a round already has exactly one country, so a country grouping adds a
 * level that can only ever hold one kind of thing, and it buries the number an
 * operator actually says out loud.
 *
 * A card shows only what answers "how is that round going" at a glance: which
 * round, for whom, how many workers, which step it is on now, how far through
 * the eight it is, and how much paperwork it holds. Everything else is inside.
 */

/**
 * One round. The whole card is the control - every part of it describes the
 * round it opens.
 *
 * The per-round tools are SIBLINGS of that card button, never children: a
 * <button> inside a <button> is invalid HTML, and browsers resolve it by
 * dropping one of them. They sit over the card's top-right corner instead, each
 * with its own stop so pressing one cannot also open the round.
 *
 * P164-R5 put PRINT among them. Owner: "ผมว่าน่าจะต้องพิมพ์จากหน้าจอ ... ภายใน
 * กล่องครับ" - the forms belong to a round, so the way to them is on the round,
 * not on a menu somewhere else that would make you name the round twice.
 */
function RoundCard({ round, canWrite, onOpen, onPrint, onRequestDelete }) {
  const percent = progressPercent(round.completedStepCount, round.stepCount);
  const openedAt = formatInstant(round.createdAtUtc);

  return (
    <li className="wx-mou-round">
      <button
        type="button"
        className="wx-mou-round-card"
        onClick={() => onOpen(round.mouCaseId)}
        aria-label={`เปิดรอบที่ ${round.roundNumber} ${round.employerName || "ยังไม่ระบุนายจ้าง"}`}
      >
        <span className="wx-mou-round-top">
          <span className="wx-mou-round-no">
            <span className="wx-mou-round-no-label">รอบที่</span>
            <strong>{round.roundNumber}</strong>
          </span>
          <span className={`wx-badge wx-badge--${caseStatusVariant(round.status)}`}>{caseStatusLabel(round.status)}</span>
        </span>

        <span className="wx-mou-round-employer">{round.employerName || "ยังไม่ระบุนายจ้าง"}</span>

        <span className="wx-mou-round-facts">
          <span className="wx-mou-round-fact">
            <Globe2 size={13} aria-hidden="true" />
            {round.countryLabel}
          </span>
          {round.workerCountRequested != null && (
            <span className="wx-mou-round-fact">
              <Users size={13} aria-hidden="true" />
              {round.workerCountRequested} คน
            </span>
          )}
          <span className="wx-mou-round-fact">
            <Paperclip size={13} aria-hidden="true" />
            {round.documentCount} ไฟล์
          </span>
        </span>

        <span className="wx-mou-round-step">
          <span className="wx-mou-round-step-no">{round.currentStepNumber}</span>
          <span className="wx-mou-round-step-title">{round.currentStepTitle}</span>
        </span>

        <span className="wx-mou-round-progress">
          <span className="wx-mou-round-progress-track">
            <span
              className={`wx-mou-round-progress-fill ${percent === 100 ? "wx-mou-round-progress-fill--complete" : ""}`}
              style={{ width: `${percent}%` }}
            />
          </span>
          <span className="wx-mou-round-progress-text">
            {round.completedStepCount}/{round.stepCount} ขั้นตอน
          </span>
        </span>

        <span className="wx-mou-round-foot">
          <span className="wx-mou-mono">{round.caseCode}</span>
          {openedAt && <span>เปิดรอบ {openedAt}</span>}
        </span>
      </button>

      <span className="wx-mou-round-tools">
        {/* Printing is a READ of the round, so it is offered to anyone who can
            see the round - a read-only viewer still has to be able to take the
            paperwork to a counter. */}
        <button
          type="button"
          className="wx-mou-round-tool"
          aria-label={`พิมพ์แบบฟอร์มของรอบที่ ${round.roundNumber} ${round.employerName || "ยังไม่ระบุนายจ้าง"}`}
          title="พิมพ์แบบฟอร์มสำหรับยื่นหน่วยงานราชการ"
          onClick={(event) => {
            event.stopPropagation();
            onPrint(round);
          }}
        >
          <Printer size={14} aria-hidden="true" />
        </button>

        {canWrite && (
          <button
            type="button"
            className="wx-mou-round-tool wx-mou-round-tool--danger"
            aria-label={`ลบรอบที่ ${round.roundNumber} ${round.employerName || "ยังไม่ระบุนายจ้าง"}`}
            onClick={(event) => {
              event.stopPropagation();
              onRequestDelete(round);
            }}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </span>
    </li>
  );
}

export default function MouRoundGrid({ rounds, canWrite, onOpen, onCreate, onPrint, onRequestDelete }) {
  return (
    <div className="wx-mou-grid-wrap">
      {rounds.length === 0 && (
        <div className="wx-mou-grid-empty">
          <p className="wx-mou-grid-empty-title">ยังไม่มีรอบการยื่น MOU ของบริษัทนี้</p>
          <p className="wx-mou-grid-empty-body">
            แต่ละรอบคือการขอแรงงาน 1 ชุด ให้นายจ้าง 1 ราย จาก 1 ประเทศ มี 8 ขั้นตอนตามแบบฟอร์มของกรมการจัดหางาน และแนบเอกสารได้ทุกขั้นตอน
          </p>
          {canWrite && (
            <button type="button" className="wx-button wx-button--primary" onClick={onCreate}>
              <Plus size={15} aria-hidden="true" />
              เปิดรอบแรก
            </button>
          )}
        </div>
      )}

      {rounds.length > 0 && (
        <ul className="wx-mou-grid">
          {rounds.map((round) => (
            <RoundCard
              key={round.mouCaseId}
              round={round}
              canWrite={canWrite}
              onOpen={onOpen}
              onPrint={onPrint}
              onRequestDelete={onRequestDelete}
            />
          ))}

          {/* The way to open a round is a card in the same grid, in the same
              place the eye already is - not a button parked somewhere else on
              the screen. It sits LAST because the rounds are newest-first and
              a new one belongs at the end of what you are reading, not on top
              of the round you probably came here to open. */}
          {canWrite && (
            <li className="wx-mou-round">
              <button type="button" className="wx-mou-round-card wx-mou-round-card--new" onClick={onCreate}>
                <span className="wx-mou-round-new-mark" aria-hidden="true">
                  <Plus size={20} />
                </span>
                <span className="wx-mou-round-new-title">เปิดรอบใหม่</span>
                <span className="wx-mou-round-new-body">ขอแรงงาน 1 ชุด ให้นายจ้าง 1 ราย จาก 1 ประเทศ</span>
              </button>
            </li>
          )}
        </ul>
      )}

      {!canWrite && rounds.length === 0 && (
        <p className="wx-mou-section-note">
          <Building2 size={14} aria-hidden="true" />
          การยื่น MOU เปิดได้เฉพาะบริษัทจัดหางาน (ฝั่งงาน) เท่านั้น บริษัทนี้อยู่ฝั่งจัดหาแรงงาน จึงไม่มีขั้นตอนการยื่นนำเข้า
        </p>
      )}
    </div>
  );
}
